import axios from 'axios';
import type { DateTime } from 'luxon';
import { merge } from 'lodash';

/**
 * ProductsService
 */
export default class ProductsService
{
    public static path = 'admin/wk/products';

    /**
     * @param filter FilterModel
     * @param pager Pager
     * @param except number[]
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager, except: number[] = []): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(this.path, {
            params: merge({}, filter, pager.data(), { except: except })
        })).data;
    }

    /**
     * @param products number[]
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getListByIds(products: number[]): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>('admin/wk/products/get-by-ids', {
            params: {
                products: products.join(',')
            }
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<ViewModel>>
     */
    public static async fetch(id: number): Promise<Resource<ViewItemModel>>
    {
        return (await axios.get<Resource<ViewItemModel>>(`${this.path}/${id}`)).data;
    }

    /**
     * @param search string
     * @param limit number
     * @param except number[]
     *
     * @returns Promise<string[]>
     */
    public static async getAutocomplete(search: string, limit: number = 10, except?: number[]): Promise<ListItemDetailsModel[]>
    {
        return (await axios.get<ListItemDetailsModel[]>(`${this.path}/autocomplete`, {
            params: {
                search: search,
                limit: limit,
                except: except
            }
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<ListItemModel>
     */
    public static async fetchAutocomplete(id: number): Promise<ListItemDetailsModel>
    {
        return (await axios.get<ListItemDetailsModel>(`${this.path}/autocomplete/${id}`)).data;
    }

    /**
     * @param widgetId number
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getListByWidget(widgetId: number): Promise<ListItemDetailsModel[]>
    {
        return (await axios.get<ListItemDetailsModel[]>(`${this.path}/by-widget/${widgetId}`)).data;
    }

    /**
     * @param articleId number
     *
     * @returns Promise<ListItemDetailsModel[]>
     */
    public static async getListByArticleId(articleId: number): Promise<ListItemDetailsModel[]>
    {
        return (await axios.get<ListItemDetailsModel[]>(`${this.path}/by-article/${articleId}`)).data;
    }
}

export interface ListItemModel
{
    id: number;
    name: string;
    picture?: File;
    authors: string;
    yearOfPublication?: number;
}

export interface ListItemDetailsModel
{
    id: number;
    name: string;
    picture?: File;
    yearOfPublication?: number;
    format: string;
    publicationVersion: string;
    coverType: string;
    state: string;
    price: number;
}

export interface ViewItemModel
{
    id: number;
    name: string;
    url: string;
    picture?: File;
    authors: string;
    edition?: number;
    format: string;
    yearOfPublication?: number;
    medium: string;
    numberOfPages?: number;
    publicationVersion: string;
    publisher: string;
    categories: string[];
    code: string;
    price: number;
    pricePromotion: number;
    historyBruttoPrice: number;
    discount: number;
    state: string;
    copyright: string;
    coverType: string;
    isbn: string;
}

export interface FilterModel
{
    name: string;
    dateCreatedFromUtc?: DateTime;
    dateCreatedDueUtc?: DateTime;
    dateModifiedFromUtc?: DateTime;
    dateModifiedDueUtc?: DateTime;
    authors: string;
    state: string;
    medium: string;
    publisher: string;
    categoryId?: number;
    publicationVersion: string;
}
